import { Link } from "react-router-dom";
import GuideLayout from "@/components/guides/GuideLayout";

const faqItems = [
  {
    question: "What is Marvelon?",
    answer:
      "Marvelon is a combined contraceptive pill made by Organon. Each tablet contains ethinylestradiol 30 micrograms and desogestrel 150 micrograms. You take one tablet a day for 21 days, then have a 7 day break, during which you usually have a withdrawal bleed.",
  },
  {
    question: "Is Marvelon the same as Gedarel?",
    answer:
      "Marvelon contains the same hormones at the same doses as Gedarel 30/150 and Cimizt: ethinylestradiol 30 micrograms and desogestrel 150 micrograms. The makers differ, but the medicine, regimen and missed pill rules are the same. Gedarel 20/150 is a lower oestrogen version and matches Mercilon instead.",
  },
  {
    question: "Can I get Marvelon free on the NHS?",
    answer:
      "Yes, if it is suitable for you. Combined pills are free on the NHS from a GP, a sexual health clinic, or a pharmacy providing the NHS Pharmacy Contraception Service. With Contraception Direct, the consultation, pill and delivery are free. A recent blood pressure reading is needed, and the brand supplied can depend on stock.",
  },
  {
    question: "What are the side effects of Marvelon?",
    answer:
      "Common side effects, affecting up to 1 in 10 users, are headache, weight gain, breast pain or tenderness, low or changed mood, nausea and stomach pain. Uncommon ones include migraine, fluid retention, lower sex drive, being sick, diarrhoea, rash and breast enlargement. Blood clots are rare but serious.",
  },
  {
    question: "Does Marvelon have a higher blood clot risk than Microgynon?",
    answer:
      "Yes, slightly. About 9 to 12 in every 10,000 people using a desogestrel combined pill such as Marvelon develop a blood clot each year, compared with about 5 to 7 for levonorgestrel pills like Microgynon, and about 2 for people not using hormonal contraception. Your pharmacist checks your risk factors first.",
  },
  {
    question: "What if I miss a Marvelon pill?",
    answer:
      "If you are less than 12 hours late, take it now and carry on as normal: the leaflet says you are still protected. If you are more than 12 hours late, take the most recent missed pill, use condoms for 7 days and follow the leaflet's advice on whether to have your break. Use our missed pill tool if unsure.",
  },
];

const tocItems = [
  { id: "what-is-it", label: "What is Marvelon?" },
  { id: "same-as", label: "Marvelon vs Gedarel" },
  { id: "how-to-take", label: "How to take it" },
  { id: "missed-pill", label: "Missed or late pills" },
  { id: "side-effects", label: "Side effects" },
  { id: "blood-clots", label: "Blood clot risk" },
  { id: "who-can-take", label: "Who can take it?" },
  { id: "getting-it", label: "How to get Marvelon free" },
];

const tableHead = "text-left px-3 py-2.5 font-semibold text-foreground border-b border-border";
const tableCell = "px-3 py-2 text-foreground/85 border-b border-border/50";
const tableFirst = "px-3 py-2 font-medium text-foreground border-b border-border/50";

const MarvelonGuide = () => {
  return (
    <GuideLayout
      title="Marvelon: What You Need to Know"
      ogImage="/og/guides/marvelon.png"
      metaTitle="Marvelon Pill: Side Effects, Clot Risk and Free NHS Supply"
      metaDescription="Marvelon explained: the same medicine as Gedarel 30/150, how to take it, the 12 hour late rule, side effects by frequency, clot risk and free NHS supply."
      canonicalPath="/guides/marvelon"
      nhsSupplyTitle="Getting Marvelon free on the NHS"
      nhsSupplyText="Marvelon is free on the NHS if it is suitable for you. A GPhC-registered pharmacist can supply combined pills through the NHS Pharmacy Contraception Service to people aged 16 to 49. A recent blood pressure reading is needed, the brand supplied (Marvelon or Gedarel 30/150) can vary with stock, and supply depends on the pharmacist's assessment."
      nhsSupplyTo="/get-the-pill-online/combined-pill/"
      nhsSupplyLabel="How to get the combined pill free on the NHS"
      breadcrumbTitle="Marvelon"
      date="October 2026"
      directAnswer="Marvelon is a combined contraceptive pill containing ethinylestradiol 30 micrograms and desogestrel 150 micrograms. You take it for 21 days, then have a 7 day break.|It is the same medicine as Gedarel 30/150. Taken correctly it is more than 99% effective, and it is free on the NHS if a pharmacist confirms it suits you."
      quickFacts={[
        "Combined pill: ethinylestradiol 30 micrograms plus desogestrel 150 micrograms.",
        "Same medicine as Gedarel 30/150 and Cimizt.",
        "21 days on, 7 days off. Less than 12 hours late: still protected.",
        "Free on the NHS for ages 16 to 49 if suitable. Blood pressure check needed.",
      ]}
      tocItems={tocItems}
      faqItems={faqItems}
      ctaTitle="Ask about Marvelon at a free consultation"
      ctaDescription="Book a free phone consultation with a pharmacist. If a combined pill is right for you, it is posted in discreet packaging within 1 to 3 working days."
      ctaButtonText="Book a free NHS pharmacist consultation"
      ctaSubtext="No GP appointment needed. Supply depends on the pharmacist's assessment."
      references={[
        { text: "EMC. Marvelon Tablets. Summary of Product Characteristics.", url: "https://www.medicines.org.uk/emc/product/1359/smpc" },
        { text: "EMC. Marvelon Tablets. Patient Information Leaflet.", url: "https://www.medicines.org.uk/emc/product/1359/pil" },
        { text: "EMC. Gedarel 30/150 microgram film-coated tablets. Summary of Product Characteristics.", url: "https://www.medicines.org.uk/emc/product/14653/smpc" },
        { text: "FSRH. Combined Hormonal Contraception Clinical Guideline, January 2019 (amended October 2023).", url: "https://www.cosrh.org/Public/Documents/fsrh-guideline-combined-hormonal-contraception" },
        { text: "NHS England. Community Pharmacy Advanced Service Specification: NHS Pharmacy Contraception Service. Version 3.0, October 2025.", url: "https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" },
        { text: "NHS. Combined pill.", url: "https://www.nhs.uk/contraception/methods-of-contraception/combined-pill/" },
      ]}
      relatedGuides={[
        { to: "/guides/what-is-the-combined-pill/", label: "What is the combined pill?" },
        { to: "/guides/gedarel/", label: "Gedarel" },
        { to: "/guides/microgynon/", label: "Microgynon 30" },
        { to: "/guides/yasmin/", label: "Yasmin" },
        { to: "/guides/contraceptive-pill-names/", label: "Contraceptive pill names" },
        { to: "/guides/pill-and-blood-clots/", label: "The pill and blood clots" },
        { to: "/guides/missed-pill-what-to-do/", label: "What to do if you miss a pill" },
        { to: "/guides/side-effects-of-the-pill/", label: "Side effects of the pill" },
        { to: "/guides/which-contraceptive-pill-is-right-for-me/", label: "Which contraceptive pill is right for me?" },
      ]}
    >
      <h2 id="what-is-it">What is Marvelon?</h2>
      <p>
        Marvelon is a combined contraceptive pill made by Organon. Each tablet contains desogestrel 150 micrograms, a progestogen, and ethinylestradiol 30 micrograms, a form of oestrogen (<a href="https://www.medicines.org.uk/emc/product/1359/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>).
      </p>
      <p>
        It works mainly by stopping your ovaries releasing an egg, and also thickens cervical mucus and thins the womb lining. Taken correctly, the combined pill is more than 99% effective, and around 91% with typical use, when pills are sometimes missed (<a href="https://www.nhs.uk/contraception/methods-of-contraception/combined-pill/" target="_blank" rel="noopener noreferrer">NHS</a>). Read our guide to <Link to="/guides/what-is-the-combined-pill/">the combined pill</Link> for how all combined pills compare.
      </p>

      <h2 id="same-as">Is Marvelon the same as Gedarel?</h2>
      <p>
        Marvelon is the same medicine as Gedarel 30/150. Both contain ethinylestradiol 30 micrograms and desogestrel 150 micrograms and follow the same 21 day pattern (<a href="https://www.medicines.org.uk/emc/product/14653/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC, Gedarel 30/150</a>). The table shows the brands with these hormones and the lower oestrogen versions.
      </p>

      <div className="overflow-x-auto my-6 -mx-2">
        <table className="w-full text-sm border-collapse border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-secondary/60">
              <th className={tableHead}>Hormones per tablet</th>
              <th className={tableHead}>Brands</th>
              <th className={tableHead}>How you take it</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Ethinylestradiol 30 micrograms + desogestrel 150 micrograms", "Marvelon, Gedarel 30/150, Cimizt", "21 days, then a 7 day break"],
              ["Ethinylestradiol 20 micrograms + desogestrel 150 micrograms", "Mercilon, Gedarel 20/150, Bimizza", "21 days, then a 7 day break"],
            ].map(([a, b, c], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className={tableFirst}>{a}</td>
                <td className={tableCell}>{b}</td>
                <td className={tableCell}>{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        Under the NHS Pharmacy Contraception Service, the brand you receive can change with stock. "A different box is not a different pill if the hormones and strength match," says Usama Ali, MPharm (GPhC: 2224726), pharmacist at Contraception Direct. "Check the two numbers on the pack. If they have changed, ask us before you start it." See <Link to="/guides/gedarel/">Gedarel</Link> for the 20/150 strength.
      </p>

      <h2 id="how-to-take">How do you take Marvelon?</h2>
      <p>
        You take one Marvelon tablet a day, at about the same time, for 21 days, then take no tablets for 7 days. A withdrawal bleed usually comes during the break. Start the next strip after the 7 days, even if you are still bleeding (<a href="https://www.medicines.org.uk/emc/product/1359/pil" target="_blank" rel="noopener noreferrer">EMC patient leaflet</a>).
      </p>
      <p>
        The leaflet prefers starting on the first day of your period, which gives protection straight away. Starting later in your cycle, or switching from another method, needs extra precautions for a time, and your pharmacist will tell you exactly when to start and whether you need condoms.
      </p>

      <h2 id="missed-pill">What if you miss or are late with a Marvelon pill?</h2>
      <p>
        If you are less than 12 hours late with a Marvelon pill, the leaflet says protection is not reduced. Take it straight away, even if that means two pills in one day, and carry on as normal (<a href="https://www.medicines.org.uk/emc/product/1359/pil" target="_blank" rel="noopener noreferrer">EMC patient leaflet</a>).
      </p>
      <p>
        If you are more than 12 hours late, or miss more than one pill, take the most recent missed pill and use condoms for the next 7 days. If fewer than 7 pills are left in the strip, the leaflet says to go straight on to the next strip without a break. Being sick within 3 to 4 hours of a pill, or severe diarrhoea for more than 12 hours, counts as a missed pill. If you had sex in the 7 days before missing pills in week 1, ask a pharmacist promptly, as emergency contraception may be needed. Our <Link to="/missed-pill/">missed pill tool</Link> can help.
      </p>

      <h2 id="side-effects">What are the side effects of Marvelon?</h2>
      <p>
        Marvelon's common side effects, reported by up to 1 in 10 users, are headache, weight gain, breast pain or tenderness, low or changed mood, nausea and stomach pain (<a href="https://www.medicines.org.uk/emc/product/1359/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). Many settle in the first few months. The table groups the listed effects by frequency.
      </p>

      <div className="overflow-x-auto my-6 -mx-2">
        <table className="w-full text-sm border-collapse border border-border rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-secondary/60">
              <th className={tableHead}>How often</th>
              <th className={tableHead}>Side effects listed for Marvelon</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Common (up to 1 in 10 users)", "Headache, weight gain, breast pain or tenderness, low or changed mood, nausea, stomach pain"],
              ["Uncommon (up to 1 in 100 users)", "Migraine, fluid retention, lower sex drive, being sick, diarrhoea, rash or hives, breast enlargement"],
              ["Rare (up to 1 in 1,000 users)", "Allergic reactions, higher sex drive, contact lens discomfort, blood clots in a vein or artery, skin reactions, vaginal or breast discharge, weight loss"],
            ].map(([a, b], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className={tableFirst}>{a}</td>
                <td className={tableCell}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>
        Bleeding between periods can happen in the first months on any combined pill. If side effects persist, talk to a pharmacist before stopping, because a different pill may suit you. See <Link to="/guides/side-effects-of-the-pill/">side effects of the pill</Link> and <Link to="/guides/bleeding-on-the-pill/">bleeding on the pill</Link>.
      </p>

      <h2 id="blood-clots">What is the blood clot risk with Marvelon?</h2>
      <p>
        Marvelon carries a slightly higher blood clot risk than levonorgestrel combined pills. About 9 to 12 in every 10,000 people using a desogestrel combined pill develop a clot in a vein each year, compared with about 5 to 7 using levonorgestrel pills such as <Link to="/guides/microgynon/">Microgynon</Link>, and about 2 not using hormonal contraception (<a href="https://www.cosrh.org/Public/Documents/fsrh-guideline-combined-hormonal-contraception" target="_blank" rel="noopener noreferrer">FSRH, 2023</a>).
      </p>
      <p>
        Your pharmacist checks your risk factors before supplying it. Call 999 or go to A&E if you notice painful swelling in one leg, sudden chest pain or breathlessness, a severe or unusual headache, sudden changes in your vision, or weakness on one side. Read more in <Link to="/guides/pill-and-blood-clots/">the pill and blood clots</Link>.
      </p>

      <h2 id="who-can-take">Who can take Marvelon?</h2>
      <p>
        Marvelon can be supplied to people aged 16 to 49 through the NHS Pharmacy Contraception Service if the pharmacist finds it suitable. A recent blood pressure reading is needed, and you will be asked about your health, medicines, smoking and family history (<a href="https://www.england.nhs.uk/publication/nhs-pharmacy-contraception-service/" target="_blank" rel="noopener noreferrer">NHS England PCS specification</a>).
      </p>
      <p>
        It is not suitable if you have had a blood clot, a heart attack or stroke, have migraine with aura, very high blood pressure, severe liver disease, or take certain hepatitis C medicines (<a href="https://www.medicines.org.uk/emc/product/1359/smpc" target="_blank" rel="noopener noreferrer">EMC SmPC</a>). If it is not suitable, the pharmacist will explain why and suggest another option, such as a <Link to="/guides/what-is-the-mini-pill/">mini pill</Link>, or a GP or sexual health clinic. The pill does not protect against sexually transmitted infections.
      </p>

      <h2 id="getting-it">How do you get Marvelon free?</h2>
      <p>
        Marvelon is free on the NHS from your GP, a sexual health clinic or a pharmacy offering the NHS Pharmacy Contraception Service. With <Link to="/get-the-pill-online/">Contraception Direct</Link>, you book a free phone consultation, your pharmacist checks your suitability, and if a combined pill suits you it is posted in plain packaging, usually within 1 to 3 working days.
      </p>
      <p>
        Weighing up your options? Read <Link to="/guides/which-contraceptive-pill-is-right-for-me/">which contraceptive pill is right for me</Link>, or book a consultation and talk it through with a pharmacist.
      </p>
    </GuideLayout>
  );
};

export default MarvelonGuide;
